'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation } from '@/hooks/useTranslation';
import { useRTL } from '@/hooks/useRTL';
import { Card, CardContent } from '@/components/ui/card';
import { Sparkles, Clock, Shield, Star, Heart, Award } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

export default function WhyChooseWhiteningSection() {
    const { t, isLoading } = useTranslation('teeth-whitening');
    const { isRTL } = useRTL();
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLDivElement>(null);
    const cardsRef = useRef<HTMLDivElement>(null);
    const statsRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isLoading) return;

        const ctx = gsap.context(() => {
            // Title animation
            if (titleRef.current) {
                gsap.fromTo(titleRef.current,
                    { opacity: 0, y: 50 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 1,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: titleRef.current,
                            start: "top 85%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }

            // Benefits cards animation
            if (cardsRef.current?.children) {
                const cards = Array.from(cardsRef.current.children);
                gsap.fromTo(cards,
                    { opacity: 0, y: 30 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.6,
                        stagger: 0.15,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: cardsRef.current,
                            start: "top 85%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }

            // Stats animation
            if (statsRef.current) {
                gsap.fromTo(statsRef.current,
                    { opacity: 0, y: 30 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.6,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: statsRef.current,
                            start: "top 85%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }

        }, sectionRef);

        return () => {
            ctx.revert();
        };
    }, [isLoading]);

    const benefits = [
        {
            icon: Sparkles,
            title: t('whyChoose.benefits.instant_results.title'),
            description: t('whyChoose.benefits.instant_results.description')
        },
        {
            icon: Clock,
            title: t('whyChoose.benefits.quick_procedure.title'),
            description: t('whyChoose.benefits.quick_procedure.description')
        },
        {
            icon: Shield,
            title: t('whyChoose.benefits.safe_effective.title'),
            description: t('whyChoose.benefits.safe_effective.description')
        },
        {
            icon: Star,
            title: t('whyChoose.benefits.professional_grade.title'),
            description: t('whyChoose.benefits.professional_grade.description')
        },
        {
            icon: Heart,
            title: t('whyChoose.benefits.confidence_boost.title'),
            description: t('whyChoose.benefits.confidence_boost.description')
        },
        {
            icon: Award,
            title: t('whyChoose.benefits.lasting_results.title'),
            description: t('whyChoose.benefits.lasting_results.description')
        }
    ];

    if (isLoading) {
        return (
            <section className="py-16 lg:py-24 bg-white dark:bg-slate-800">
                <div className="container">
                    <div className="text-center mb-16 animate-pulse">
                        <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded mb-6 max-w-4xl mx-auto"></div>
                        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded max-w-3xl mx-auto"></div>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                        {Array.from({ length: 6 }).map((_, index) => (
                            <Card key={index} className="h-full animate-pulse">
                                <CardContent className="p-8 text-center h-full flex flex-col">
                                    <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-2xl mx-auto mb-6"></div>
                                    <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
                                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mx-auto"></div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section id="why-whitening" ref={sectionRef} className="py-16 lg:py-24 bg-white dark:bg-slate-800">
            <div className="container">
                <div ref={titleRef} className="text-center mb-16">
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy dark:text-white font-serif leading-tight mb-6">
                        {t('whyChoose.title.part1')} <span className="text-gold">{t('whyChoose.title.part2')}</span>
                    </h2>
                    <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
                        {t('whyChoose.subtitle')}
                    </p>
                </div>

                <div ref={cardsRef} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                    {benefits.map((benefit, index) => (
                        <Card key={index} className="h-full border-slate-200 dark:border-slate-700 hover:shadow-xl transition-all duration-300 group hover:-translate-y-2 hover:border-gold/50 bg-gradient-to-br from-white to-slate-50 dark:from-slate-700 dark:to-slate-800">
                            <CardContent className="p-8 text-center h-full flex flex-col">
                                <div className="w-16 h-16 bg-gold/20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:bg-gold/30 transition-all duration-300 group-hover:scale-110">
                                    <benefit.icon className="w-8 h-8 text-gold" />
                                </div>

                                <h3 className="text-xl font-bold text-navy dark:text-white mb-4">
                                    {benefit.title}
                                </h3>

                                <p className="text-slate-600 dark:text-slate-300 leading-relaxed flex-1 flex items-center text-center">
                                    {benefit.description}
                                </p>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {/* Statistics Section */}
                <div ref={statsRef} className="max-w-4xl mx-auto mt-16">
                    <Card className="bg-gold/10 border-gold/30">
                        <CardContent className="p-8 text-center">
                            <div className={`flex items-center justify-center gap-3 mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                <Sparkles className="w-8 h-8 text-gold" />
                                <h3 className="text-2xl font-bold text-navy dark:text-white">
                                    {t('whyChoose.stats.title')}
                                </h3>
                            </div>

                            <div className="grid md:grid-cols-3 gap-8">
                                <div className={`${isRTL ? 'text-right' : 'text-center'}`}>
                                    <div className={`text-3xl font-bold text-gold mb-2 ${isRTL ? 'text-right' : 'text-center'}`}>{t('whyChoose.stats.satisfaction.number')}</div>
                                    <div className={`text-sm text-slate-600 dark:text-slate-300 ${isRTL ? 'text-right' : 'text-center'}`}>
                                        {t('whyChoose.stats.satisfaction.title')}<br />
                                        <span className="text-xs opacity-75">{t('whyChoose.stats.satisfaction.subtitle')}</span>
                                    </div>
                                </div>

                                <div className={`${isRTL ? 'text-right' : 'text-center'}`}>
                                    <div className={`text-3xl font-bold text-gold mb-2 ${isRTL ? 'text-right' : 'text-center'}`}>{t('whyChoose.stats.procedures.number')}</div>
                                    <div className={`text-sm text-slate-600 dark:text-slate-300 ${isRTL ? 'text-right' : 'text-center'}`}>
                                        {t('whyChoose.stats.procedures.title')}<br />
                                        <span className="text-xs opacity-75">{t('whyChoose.stats.procedures.subtitle')}</span>
                                    </div>
                                </div>

                                <div className={`${isRTL ? 'text-right' : 'text-center'}`}>
                                    <div className={`text-3xl font-bold text-gold mb-2 ${isRTL ? 'text-right' : 'text-center'}`}>{t('whyChoose.stats.whitening_levels.number')}</div>
                                    <div className={`text-sm text-slate-600 dark:text-slate-300 ${isRTL ? 'text-right' : 'text-center'}`}>
                                        {t('whyChoose.stats.whitening_levels.title')}<br />
                                        <span className="text-xs opacity-75">{t('whyChoose.stats.whitening_levels.subtitle')}</span>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </section>
    );
}